import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import type { IAuthState, IUser, UserRole } from "../types/auth.types";
import { login, register } from "../api/api";
import type { LoginFormData } from "../types/login.types";
import type { RegistrationFormData } from "../types/registration.types";

// Helper to get initial state from localStorage
const getInitialState = (): IAuthState => {
  const token = localStorage.getItem("dealer_token");
  const spotfixToken = localStorage.getItem("spotfix_token");
  const role = localStorage.getItem("dealer_role") as UserRole | null;
  const userStr = localStorage.getItem("dealer_user");
  let user: IUser | null = null;

  if (userStr) {
    try {
      user = JSON.parse(userStr);
    } catch (e) {
      console.error("Failed to parse user from localStorage", e);
    }
  }

  return {
    token,
    spotfixToken,
    role,
    user,
    loading: false,
    error: null,
  };
};

const initialState: IAuthState = getInitialState();

export const loginUser = createAsyncThunk(
  "auth/login",
  async (data: LoginFormData, { rejectWithValue }) => {
    try {
      const response = await login(data);
      console.log("[AuthSlice Debug] Raw login response data:", response.data);

      if (!response?.data?.data?.dealer || !response?.data?.data?.token) {
        throw new Error("Invalid login response structure");
      }

      const spotfixToken = (response.data.data as any).spotfixToken || (response.data.data.dealer as any)?.spotfixToken || null;

      // Map the dealer response to the user structure expected by Redux
      const user = {
        userId: response.data.data.dealer.id?.toString() || "",
        firstName: response.data.data.dealer.firstName || "",
        lastName: response.data.data.dealer.lastName || "",
        businessEmail: response.data.data.dealer.email || "",
        email: response.data.data.dealer.email || "",
        companyName: response.data.data.dealer.companyName || "",
        tier: response.data.data.dealer.tier?.name || "Standard",
        tierId: response.data.data.dealer.tierId,
        revenue365: response.data.data.dealer.revenue365,
        spotfixToken,
      };

      return {
        sessionId: (response.data.data as any).sessionId,
        token: response.data.data.token,
        spotfixToken,
        role: "dealer" as UserRole,
        user: user,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Login failed"
      );
    }
  }
);

export const registerUser = createAsyncThunk(
  "auth/register",
  async (data: RegistrationFormData, { rejectWithValue }) => {
    try {
      const response = await register(data);

      if (!response?.data?.data?.dealer || !response?.data?.data?.token) {
        throw new Error("Invalid registration response structure");
      }

      // Map the dealer response to the user structure expected by Redux
      const user = {
        userId: response.data.data.dealer.id?.toString() || "",
        firstName: response.data.data.dealer.firstName || "",
        lastName: response.data.data.dealer.lastName || "",
        businessEmail: response.data.data.dealer.email || "",
        email: response.data.data.dealer.email || "",
        companyName: response.data.data.dealer.companyName || "",
        tier: response.data.data.dealer.tier?.name, // Use the tier name from response if available
        tierId: response.data.data.dealer.tierId,
        revenue365: response.data.data.dealer.revenue365,
      };

      return {
        sessionId: (response.data.data as any).sessionId,
        token: response.data.data.token,
        role: "dealer" as UserRole,
        user: user,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Registration failed"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSuccess: (
      state,
      action: PayloadAction<{
        token: string;
        spotfixToken?: string | null;
        role: UserRole;
        user: IUser;
        sessionId?: string;
      }>
    ) => {
      state.token = action.payload.token;
      state.spotfixToken = action.payload.spotfixToken || action.payload.user?.spotfixToken || null;
      state.role = action.payload.role;
      state.user = action.payload.user;
      state.error = null;

      // Persist to localStorage
      localStorage.setItem("dealer_token", action.payload.token);
      if (state.spotfixToken) {
        localStorage.setItem("spotfix_token", state.spotfixToken);
      }
      if ((action.payload as any).sessionId) {
        localStorage.setItem("dealer_activity_session_id", (action.payload as any).sessionId);
      }
      localStorage.setItem("dealer_role", action.payload.role);
      localStorage.setItem("dealer_user", JSON.stringify(action.payload.user));
    },

    logout: (state) => {
      state.token = null;
      state.spotfixToken = null;
      state.role = null;
      state.user = null;
      state.error = null;

      // Clear localStorage
      localStorage.removeItem("dealer_token");
      localStorage.removeItem("spotfix_token");
      localStorage.removeItem("dealer_role");
      localStorage.removeItem("dealer_user");
      localStorage.removeItem("dealer_survey_dismissed");
      try {
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith("dealer_inportal_cart")) {
            localStorage.removeItem(key);
          }
        });
      } catch (e) {
        console.warn("Failed to clear cart storage on logout", e);
      }

      // Cleanup Spotfix SDK from DOM
      if (typeof window !== "undefined") {
        if (window.SpotFix?.logout) {
          try {
            window.SpotFix.logout();
          } catch (e) {
            console.warn("[SpotFix] Error logging out", e);
          }
        }
        const script = document.getElementById("spotfix-sdk-script");
        if (script) script.remove();
        const widgetElements = document.querySelectorAll(
          '[class*="spotfix"], [id*="spotfix"], [class*="viewtheme"], [id*="viewtheme"]'
        );
        widgetElements.forEach((el) => el.remove());
      }
    },

    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<Partial<IUser>>) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
        };
        // Persist to localStorage
        localStorage.setItem("dealer_user", JSON.stringify(state.user));
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.spotfixToken = (action.payload as any).spotfixToken || null;
        state.role = action.payload.role;
        state.user = action.payload.user;

        localStorage.setItem("dealer_token", action.payload.token);
        if (state.spotfixToken) {
          localStorage.setItem("spotfix_token", state.spotfixToken);
        }
        if ((action.payload as any).sessionId) {
          localStorage.setItem("dealer_activity_session_id", (action.payload as any).sessionId);
        }
        localStorage.setItem("dealer_role", action.payload.role);
        localStorage.setItem("dealer_user", JSON.stringify(action.payload.user));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.role = action.payload.role;
        state.user = action.payload.user;

        localStorage.setItem("dealer_token", action.payload.token);
        if ((action.payload as any).sessionId) {
          localStorage.setItem("dealer_activity_session_id", (action.payload as any).sessionId);
        }
        localStorage.setItem("dealer_role", action.payload.role);
        localStorage.setItem("dealer_user", JSON.stringify(action.payload.user));
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Auto-sync account info from dealer profile actions
    builder.addCase("dealer/fetchProfile/fulfilled", (state, action: any) => {
      if (action.payload?.spotfixToken) {
        state.spotfixToken = action.payload.spotfixToken;
        localStorage.setItem("spotfix_token", action.payload.spotfixToken);
      }
      if (state.user && action.payload) {
        state.user = {
          ...state.user,
          firstName: action.payload.firstName || state.user.firstName,
          lastName: action.payload.lastName || state.user.lastName,
          companyName: action.payload.companyName || state.user.companyName,
          businessEmail: action.payload.email || state.user.businessEmail,
          email: action.payload.email || state.user.email,
          spotfixToken: action.payload.spotfixToken || state.user.spotfixToken,
        };
        localStorage.setItem("dealer_user", JSON.stringify(state.user));
      }
    });

    builder.addCase("dealer/updateProfile/fulfilled", (state, action: any) => {
      if (state.user && action.payload) {
        state.user = {
          ...state.user,
          firstName: action.payload.firstName || state.user.firstName,
          lastName: action.payload.lastName || state.user.lastName,
          companyName: action.payload.companyName || state.user.companyName,
          businessEmail: action.payload.email || state.user.businessEmail,
          email: action.payload.email || state.user.email,
        };
        localStorage.setItem("dealer_user", JSON.stringify(state.user));
      }
    });
  },
});

export const { loginSuccess, logout, clearError, updateUser } = authSlice.actions;
export default authSlice.reducer;
